import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-hot-toast';
import './Checkout.scss';
import SvgIcon from '@Components/icons/SvgIcon';
import { AppRouteURL } from '@Constants/app-route-url.constant';
import { getStudentPlanDetails, submitCouponCode, subscribeStudentPlan, subscribeStudentPlanPayment } from './services/Checkout.service';
import Loader from '@Components/loader/Loader';
import { NoSubscriptionDialog } from '@Components/NoSubscriptionDialog/NoSubscriptionDialog';
import { getUserDetails } from '../UserProfile/Services/UserProfile.sevice';
import { useAppDispatch, useAppSelector } from '@Src/app/hooks';
import { showSpinner } from '@Src/app/reducers/globalSlice';

export const CheckoutIndex = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { plan: initialPlan } = location.state || {};
    const [plan, setPlan] = React.useState<any>(initialPlan);
    const [loading, setLoading] = React.useState(true);
    const [isCouponApplied, setIsCouponApplied] = React.useState(false);
    const [couponError, setCouponError] = React.useState('');
    const [discountPercentage, setDiscountPercentage] = React.useState<string>('');
    const [couponId, setCouponId] = React.useState<number | null>(null);
    const [showDialog, setShowDialog] = React.useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = React.useState(false);
    const [phoneNumber, setPhoneNumber] = React.useState('');
    const firstName = useAppSelector(state => state.login.Firstname);
    const lastName = useAppSelector(state => state.login.Lastname);
    const email = useAppSelector(state => state.login.Email);
    const instituteId = useAppSelector(state => state.login.InstituteId);

    useEffect(() => {
        if(instituteId){
            navigate('/');
        }
    }, [instituteId]);

    const formik = useFormik({
        initialValues: {
            couponCode: ''
        },
        // validationSchema: Yup.object({
        //     couponCode: Yup.string().min(3, 'Coupon code must be at least 3 characters')
        // }),
        onSubmit: async (values) => {
            const payload: any = couponId ? {
                PlanId: plan?.id,
                CouponCodeId: couponId,
                OriginalPrice: plan?.Price,
                DicsountPercentage: discountPercentage,
                DiscountPrice: discountAmount,
                Price: formattedDiscountedPrice
            } : {
                PlanId: plan?.id,
                Price: plan?.Price

            };





            try {
                dispatch(showSpinner(true));
                const orderRes = await subscribeStudentPlanPayment({ amount: payload.Price });
                if (orderRes.ResponseType === 'SUCCESS' && orderRes?.data[0]?.amount) {
                    dispatch(showSpinner(false));
                    const options = {
                        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                        amount: orderRes.data[0].amount,
                        currency: orderRes.data[0].currency,
                        name: "Iabhyas",
                        description: "Test Payment",
                        order_id: orderRes.data.id,
                        handler: async function (response: any) {
                            try {
                                dispatch(showSpinner(true));
                                const subscribeRes = await subscribeStudentPlan({ ...payload, OrderId: orderRes.data[0].id, PaymentId: response.razorpay_payment_id });
                                if (subscribeRes.ResponseType === 'SUCCESS') {
                                    setShowSuccessDialog(true);
                                } else {
                                    toast.error(subscribeRes.StatusMsg || 'Subscription failed. Please contact support.');
                                }
                            } catch (error) {
                                console.error('Error during final subscription step:', error);
                                toast.error('An error occurred. Please contact support.');
                            } finally {
                                dispatch(showSpinner(false));
                            }
                        },
                        prefill: {
                            name: `${firstName} ${lastName}`,
                            email: email,
                            contact: phoneNumber,
                        },
                        modal: {
                            ondismiss: function () {
                                dispatch(showSpinner(false));
                            }
                        },
                        theme: {
                            color: "#7A68E0",
                        },
                    };

                    const rzp = new (window as any).Razorpay(options);
                    rzp.open();

                } else {
                    dispatch(showSpinner(false));
                    toast.error(orderRes.StatusMsg || 'Subscription failed. Please try again.');
                }
            } catch (error) {
                dispatch(showSpinner(false));
                toast.error('Subscription failed. Please try again.');
            } finally {
                formik.setSubmitting(false);
            }


            // try {
            //     const response = await subscribeStudentPlan(payload);
            //     if (response.ResponseType === 'SUCCESS') {
            //         // toast.success('Subscription successful!');
            //         setShowDialog(false);
            //         setShowSuccessDialog(true);
            //     } else {
            //         toast.error('Subscription failed. Please try again.');
            //     }
            // } catch (error) {
            //     toast.error('Subscription failed. Please try again.');
            // } finally {
            //     formik.setSubmitting(false);
            // }
        }
    });

    React.useEffect(() => {
        const initializeCheckout = async () => {
            try {
                setLoading(true);
                // First check if user is already subscribed
                const userResponse = await getUserDetails();
                if (userResponse.success && userResponse.data?.[0]) {
                    const userData = userResponse.data[0];
                    const studentSubPlanName = userData.Student_sub_PlanName;
                    const studentSubEndDate = userData.Student_sub_EndDate;
                    const studentSubBalance = userData.Student_sub_BalanceNumberOfMocktests;
                    const studentSubPlanPrice = userData.Student_sub_Price;
                    setPhoneNumber(userData.PhoneNo || '');
                    if (studentSubPlanName && studentSubEndDate) {
                        const isActive = new Date(studentSubEndDate) >= new Date() && (studentSubBalance ?? 0) > 0;
                        if (isActive && !(Number(studentSubPlanPrice) === 0)) {
                            navigate('/');
                            return;
                        }
                    }
                }

                // If not subscribed, fetch plan details
                if (initialPlan?.Id) {
                    await fetchPlanDetails(initialPlan.Id);
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error initializing checkout:', error);
                setLoading(false);
            }
        };

        initializeCheckout();
    }, [initialPlan, navigate]);

    const fetchPlanDetails = async (planId: number) => {
        try {
            const response = await getStudentPlanDetails(planId);
            if (response.success && response.data) {
                setPlan(response.data);
            }
        } catch (error) {
            console.error('Error fetching plan details:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!initialPlan) {
        return (
            <div className="checkout-page-wrapper">
                <div className="ia-container tw-text-center py-5">
                    <h1 className="tw-text-2xl tw-font-bold mb-4">No Plan Selected</h1>
                    <button className="ia-btn ia-btn-primary" onClick={() => navigate(AppRouteURL.SUBSCRIPTION)}>
                        Back to Subscriptions
                    </button>
                </div>
            </div>
        );
    }

    const priceNum = parseFloat(plan?.Price || '0');
    const formattedPrice = priceNum % 1 === 0 ? priceNum.toFixed(0) : plan?.Price;
    const discountAmount = discountPercentage ? (priceNum * parseFloat(discountPercentage) / 100).toFixed(2) : null;
    const discountedPrice = discountPercentage ? (priceNum - parseFloat(discountAmount!)).toFixed(2) : null;
    const formattedDiscountedPrice = discountedPrice && parseFloat(discountedPrice) % 1 === 0 ? parseFloat(discountedPrice).toFixed(0) : discountedPrice;


    const couponCodeApply = async () => {
        if (!formik.values.couponCode.trim()) {
            setCouponError('Please enter a valid coupon code to apply');
            return;
        }
        try {
            const response = await submitCouponCode(formik.values.couponCode);
            if (response.ResponseType === 'SUCCESS' && response.data?.[0]) {
                setDiscountPercentage(response.data[0].discountPercentage);
                setCouponId(response.data[0].couponId);
                setCouponError('');
                setIsCouponApplied(true);
            }
        } catch (error: any) {
            let errorMsg = 'Invalid coupon code';
            if (error.response.data.StatusMsg === 'COUPON_EXPIRED') errorMsg = 'Coupon code has expired';
            else if (error.response.data.StatusMsg === 'INVALID_COUPON_CODE') errorMsg = 'Invalid coupon code';
            else if (error.response.data.StatusMsg === 'COUPON_NOT_STARTED') errorMsg = 'Coupon code has not started yet';

            setCouponError(errorMsg);

        }
    }


    if (loading) {
        return (
            <div className="checkout-page-wrapper">
                <Loader />
            </div>
        );
    }



    return (
        <div className="checkout-page-wrapper">
            <div className="ia-container">
                <div className="checkout-two-column">
                    <div className="order-summary-section">
                        <button className="back-nav-btn" onClick={() => navigate(-1)}>
                            <SvgIcon name="rightArrow" size={18} style={{ transform: 'rotate(180deg)' }} />
                        </button>

                        <div className="vendor-info mt-4">
                            <div className="vendor-logo-placeholder primary">
                                <SvgIcon name="crown" size={20} />
                            </div>
                            <span className="vendor-name">Iabhyas</span>
                        </div>

                        <div className="price-display mt-5">
                            <span className="price-label">Pay Iabhyas</span>
                            {discountedPrice ? (
                                <>
                                    <h1 className="main-price tw-line-through tw-opacity-50">₹{formattedPrice}</h1>
                                    <div className="tw-flex tw-items-center tw-gap-3 tw-mt-2 tw-w-full">
                                        <h1 className="main-price tw-m-0">₹{formattedDiscountedPrice}</h1>
                                        <span className="tw-text-green-600 tw-font-semibold tw-text-lg">{parseFloat(discountPercentage).toFixed(0)}% off</span>
                                        <span className="ia-badge-coupon tw-ml-auto">coupon applied</span>
                                    </div>
                                </>
                            ) : (
                                <h1 className="main-price">₹{formattedPrice}</h1>
                            )}
                            {
                                plan.Duration && plan.Uom && <div className="feature-item" style={{color:"#575757", fontWeight:600}}>
                                    <span>Duration - {plan.Duration} {plan.Uom === 'month' ? 'Month' : plan.Uom}{plan.Duration > 1 ? 's' : ''}</span>
                                </div>
                            }
                        </div>

                        <div className="order-features-list mt-5">
                            <h3 className="features-title">Benefits</h3>
                            {
                                plan.NumberOfMocktests && <div className="feature-item">
                                    <SvgIcon name="tick" size={18} className="tick-icon" />
                                    <span>Mocktest - {plan.NumberOfMocktests}</span>
                                </div>
                            }

                            {plan.ShowAnalytics && (
                                <div className="feature-item">
                                    <SvgIcon name="tick" size={18} className="tick-icon" />
                                    <span>Performance Analysis and Tips</span>
                                </div>
                            )}
                        </div>

                        <div className="checkout-footer-info">
                            <div className="powered-by">
                                <span>Powered by</span>
                                <span className="powered-name-bold ml-1">iabhyas</span>
                            </div>
                            <div className="footer-links tw-cursor-pointer" onClick={()=>navigate(AppRouteURL.TERMS_AND_CONDITIONS)}>
                                <span>Terms & Conditions</span>
                            
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Payment Form */}
                    <div className="payment-form-section">
                        <h2 className="form-title">Payment </h2>



                        <form className="mt-4" onSubmit={formik.handleSubmit}>
                            <div className="form-group mt-4">
                                <label>Coupon Code</label>
                                <div className="tw-flex tw-gap-2">
                                    <div className="tw-flex-1 tw-relative">
                                        <input
                                            type="text"
                                            name="couponCode"
                                            placeholder="Promo code"
                                            className="ia-input"
                                            value={formik.values.couponCode}
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                setCouponError('');
                                            }}
                                            onBlur={formik.handleBlur}
                                            disabled={isCouponApplied}
                                        />
                                        {isCouponApplied && (
                                            <button
                                                type="button"
                                                className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600"
                                                onClick={() => {
                                                    setIsCouponApplied(false);
                                                    formik.setFieldValue('couponCode', '');
                                                    setCouponError('');
                                                    setDiscountPercentage('');
                                                    setCouponId(null);
                                                }}
                                            >
                                                <SvgIcon name="cross" size={16} />
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className={`ia-btn-text-link tw-whitespace-nowrap ${isCouponApplied ? 'applied' : ''}`}
                                        onClick={() => { couponCodeApply() }}
                                    >
                                        {isCouponApplied ? 'Applied' : 'Apply'}
                                    </button>
                                </div>
                                {couponError && (
                                    <div className="tw-text-red-500 tw-text-sm tw-mt-1">{couponError}</div>
                                )}
                            </div>

                            <button
                                type="submit"
                                className={`ia-btn ia-btn-primary full-width-btn mt-5 subscribe-btn ${formik.isSubmitting ? 'processing' : ''}`}
                                disabled={formik.isSubmitting}
                            >
                                {formik.isSubmitting ? 'Processing...' : 'Subscribe'}
                            </button>

                            <p className="form-disclaimer mt-4">
                                By confirming your subscription, you allow Iabhyas to charge your card for this payment and future payments in accordance with their terms.
                            </p>
                        </form>

                        <div className="checkout-footer-info mobile-only">
                            <div className="powered-by">
                                <span>Powered by</span>
                                <span className="powered-name-bold ml-1">iabhyas</span>
                            </div>
                            <div className="footer-links tw-cursor-pointer" onClick={()=>navigate(AppRouteURL.TERMS_AND_CONDITIONS)}>
                                <span>Terms & Conditions</span>
                                
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* <NoSubscriptionDialog
                visible={showDialog}
                onHide={() => setShowDialog(false)}
                onSubscribe={() => formik.handleSubmit()}
                heading="Do you want to subscribe?"
                message=" "
                showSubscribeButton={true}
                primaryButtonText="Yes"
                secondaryButtonText="No"
                position="top"
            /> */}

            <NoSubscriptionDialog
                visible={showSuccessDialog}
                onHide={() => {
                    setShowSuccessDialog(false);
                    navigate(-2);
                }}
                heading="You have subscribed successfully"
                message=" "
                showSubscribeButton={false}
                primaryButtonText="Ok"
                position="center"
            />
        </div>
    );
};
